import { QueryClient, QueryFunction, Query } from "@tanstack/react-query";

export class ApiError extends Error {
  readonly status: number;
  readonly responseText: string;

  constructor(status: number, responseText: string) {
    super(`${status}: ${responseText}`);
    this.name = "ApiError";
    this.status = status;
    this.responseText = responseText;
  }
}

export function isForbiddenError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 403;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, text);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      // Session expired — immediately clear auth so the app shows the login page
      // without requiring a manual refresh.
      queryClient.setQueryData(["/api/auth/me"], null);
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * Invalidate every cached query whose first key segment starts with `prefix`.
 * Handles parameterised URLs (e.g. "/api/call-logs?page=2") correctly — use
 * this instead of bare `invalidateQueries({ queryKey: [exactUrl] })` so that
 * filtered / paginated variants are also refreshed.
 */
export function invalidatePrefix(prefix: string): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query: Query) =>
      typeof query.queryKey[0] === "string" &&
      (query.queryKey[0] as string).startsWith(prefix),
  });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
